import { useState } from 'react';
import PropTypes from 'prop-types';

import InputField from '@components/inputs/input';
import Select from '@components/select';
import Link from '@components/link';
import Button from '@components/button';
import CalculatorLayout from '../shared/calculator-layout';
import ChartTooltip from '../shared/chart-tooltip';
import CalculatorLegend from '../shared/calculator-legend';
import CalculatorLineChart from '../shared/calculator-line-chart';

import { calculateRefinance, formatCurrency } from '../shared/calculator-utils';
import useInterestRate from '../shared/use-interest-rate';
import useLoanTerm from '../shared/use-loan-term';
import {
  LOAN_TERM_OPTIONS,
  CTA_BUTTON_CONFIG,
  DISCLAIMER_URLS
} from '../shared/calculator-ui-constants';

import styles from './styles.module.scss';
const {
  calculator_cta,
  results_header,
  savings_display,
  savings_amount,
  savings_period,
  cta_wrapper,
  tooltip_header,
  tooltip_row,
  tooltip_summary,
  select_wrapper
} = styles;

const RefinanceCalculator = ({
  calculatorOptions = [],
  onCalculatorChange,
  showCalculatorSelector = true,
  currentRates,
  hideVisual = false,
  condensed = false,
  title,
  description
}) => {
  const [currentPayment, setCurrentPayment] = useState(650);
  const [currentLoanBalance, setCurrentLoanBalance] = useState(120000);
  const [closingCosts, setClosingCosts] = useState(3000);

  // Use custom hooks for rate and term management
  const {
    interestRate: newInterestRate,
    handleInterestRateChange,
    setInterestRate
  } = useInterestRate(currentRates?.[30] || 4);

  const { loanTerm: newTermYears, handleLoanTermChange: handleNewTermChange } = useLoanTerm(
    30,
    currentRates,
    setInterestRate
  );

  const refinanceAnalysis = calculateRefinance(
    currentLoanBalance,
    currentPayment,
    newInterestRate,
    newTermYears,
    closingCosts
  );

  const generateChartData = () => {
    const data = [];
    const breakEvenMonth = refinanceAnalysis.breakEvenMonths;

    // Dynamic chart range: extend to 2x break-even, with min 24 and max 120 months
    const maxMonths = Math.min(120, Math.max(24, Math.ceil(breakEvenMonth * 2)));

    // Generate regular data points at consistent intervals for smooth lines
    for (let month = 0; month <= maxMonths; month += 1) {
      const currentLoanCost = refinanceAnalysis.currentPayment * month;
      const refinanceCost = refinanceAnalysis.newPayment * month + closingCosts;

      data.push({
        month: month,
        year: month / 12,
        currentLoan: currentLoanCost,
        refinance: refinanceCost
      });
    }

    return data;
  };

  const chartData = generateChartData();

  // Custom tooltip component for Recharts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const currentCost = payload.find((p) => p.dataKey === 'currentLoan')?.value || 0;
      const refinanceCost = payload.find((p) => p.dataKey === 'refinance')?.value || 0;
      const isBreakEven = Math.abs(label - refinanceAnalysis.breakEvenMonths) < 1;

      return (
        <ChartTooltip>
          <p className={tooltip_header}>
            Month {label}
            {isBreakEven && ' (Break-Even)'}
          </p>
          <p className={`${tooltip_row} current`}>Current Loan: {formatCurrency(currentCost)}</p>
          <p className={`${tooltip_row} refinance`}>Refinance: {formatCurrency(refinanceCost)}</p>
          <p className={tooltip_summary}>
            {refinanceCost < currentCost
              ? `Saving: ${formatCurrency(currentCost - refinanceCost)}`
              : `Cost: ${formatCurrency(refinanceCost - currentCost)}`}
          </p>
        </ChartTooltip>
      );
    }
    return null;
  };

  CustomTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.arrayOf(PropTypes.object),
    label: PropTypes.number
  };

  const disclaimerContent = (
    <>
      *Refinance analysis is based on estimated values and current market rates.
      <br />
      Actual savings depend on your credit profile, loan-to-value ratio, and closing costs.{' '}
      <Link url={DISCLAIMER_URLS.findLoanOfficer}>Contact a loan officer</Link> for a personalized
      refinance analysis.
    </>
  );

  const isSaving = refinanceAnalysis.monthlySavings > 0;

  const legendItems = [
    {
      label: 'CURRENT MONTHLY PAYMENT',
      value: refinanceAnalysis.currentPayment,
      color: '#666'
    },
    {
      label: 'NEW MONTHLY PAYMENT',
      value: refinanceAnalysis.newPayment,
      color: '#8B0000'
    },
    {
      label: 'MONTHS TO BREAK EVEN',
      value: refinanceAnalysis.breakEvenMonths,
      color: '#000',
      isInteger: true,
      displayValue: isSaving ? undefined : 'N/A'
    }
  ];

  const resultsContent = (
    <>
      <h3 className={results_header}>{isSaving ? 'You could save' : 'This would cost you'}</h3>

      <div className={savings_display}>
        <div className={savings_amount}>
          {formatCurrency(Math.abs(refinanceAnalysis.monthlySavings))}
        </div>
        <div className={savings_period}>/month</div>
      </div>

      <CalculatorLegend items={legendItems} condensed={condensed} hideVisual={hideVisual} />

      <CalculatorLineChart
        data={chartData}
        lines={[
          { dataKey: 'currentLoan', stroke: '#666', name: 'Current Loan' },
          { dataKey: 'refinance', stroke: '#8B0000', name: 'Refinance' }
        ]}
        yAxisFormatter={(value) => Math.round(value)}
        tooltip={<CustomTooltip />}
        breakEvenPoint={{
          month: refinanceAnalysis.breakEvenMonths,
          value: refinanceAnalysis.currentPayment * refinanceAnalysis.breakEvenMonths
        }}
        condensed={condensed}
        hideVisual={hideVisual}
      />

      <div className={cta_wrapper}>
        <Button
          type="button"
          label={CTA_BUTTON_CONFIG.label}
          url={CTA_BUTTON_CONFIG.url}
          theme={CTA_BUTTON_CONFIG.theme}
          className={calculator_cta}
        />
      </div>
    </>
  );

  return (
    <CalculatorLayout
      title={title || 'Refinance Calculator'}
      description={description || 'See if refinancing your mortgage makes sense'}
      calculatorType="refinance"
      calculatorOptions={calculatorOptions}
      onCalculatorChange={onCalculatorChange}
      showCalculatorSelector={showCalculatorSelector}
      hideVisual={hideVisual}
      condensed={condensed}
      resultsContent={resultsContent}
      disclaimerContent={disclaimerContent}
    >
      <InputField
        label="Current Payment"
        inputType="text"
        inputFormat="currency"
        defaultInputValue={currentPayment.toString()}
        defaultOnChange={(value) => setCurrentPayment(parseFloat(value) || 0)}
        inputName="currentPayment"
      />

      <InputField
        label="Current Loan Balance"
        inputType="text"
        inputFormat="currency"
        defaultInputValue={currentLoanBalance.toString()}
        defaultOnChange={(value) => setCurrentLoanBalance(parseFloat(value) || 0)}
        inputName="currentLoanBalance"
      />

      <InputField
        label="New Interest Rate"
        inputType="text"
        inputFormat="percentage"
        defaultInputValue={newInterestRate.toString()}
        defaultOnChange={handleInterestRateChange}
        inputName="newInterestRate"
      />

      <div className={select_wrapper}>
        <Select
          id="newTermYears"
          label="New Term Years"
          options={LOAN_TERM_OPTIONS}
          selected={newTermYears}
          onChange={handleNewTermChange}
        />
      </div>

      <InputField
        label="Closing Costs"
        inputType="text"
        inputFormat="currency"
        defaultInputValue={closingCosts.toString()}
        defaultOnChange={(value) => setClosingCosts(parseFloat(value) || 0)}
        inputName="closingCosts"
      />
    </CalculatorLayout>
  );
};

RefinanceCalculator.propTypes = {
  calculatorOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    })
  ),
  onCalculatorChange: PropTypes.func,
  showCalculatorSelector: PropTypes.bool,
  currentRates: PropTypes.shape({
    15: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    30: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  hideVisual: PropTypes.bool,
  condensed: PropTypes.bool,
  title: PropTypes.string,
  description: PropTypes.string
};

export default RefinanceCalculator;
